import {
  AbstractMesh,
  Color3,
  Color4,
  ColorGradient,
  ConeParticleEmitter,
  GPUParticleSystem,
  NoiseProceduralTexture,
  ParticleHelper,
  ParticleSystem,
  ParticleSystemSet,
  SubEmitter,
  SubEmitterType,
  Texture,
  Vector3,
} from '@babylonjs/core';
import { Builder, IBuilder } from 'builder-pattern';
import { Game } from './Game';
import { Level } from './Level';

export class FogAreaConfig {
  static builder(name: string, color?: Color3, minEmitBox?: Vector3, maxEmitBox?: Vector3): IBuilder<FogAreaConfig> {
    return Builder(FogAreaConfig, {
      name,
      color,
      minEmitBox,
      maxEmitBox,
    });
  }
  /**
   * Name of the wind area
   */
  name: string;
  /**
   * Fog color in the area
   */
  color?: Color3;
  /**
   * Min emit box for the fog area
   */
  minEmitBox?: Vector3;
  /**
   * Max emit box for the fog area
   */
  maxEmitBox?: Vector3;
}

/**
 * Particles class to manage different particle systems in the game.
 *
 * Tester
 * https://playground.babylonjs.com/#NNL67B#72
 *
 *
 * TODO:
 * https://playground.babylonjs.com/#K7KKZ1#1
 * https://playground.babylonjs.com/#8LPIS0#0
 * https://playground.babylonjs.com/#WJBZQH#53
 * https://playground.babylonjs.com/#3BSOPO#1
 * https://playground.babylonjs.com/#7GCF37#0
 * https://playground.babylonjs.com/#BHNVUE#301
 * https://playground.babylonjs.com/#6EGCCM#11
 * https://playground.babylonjs.com/#EV0SEQ#13
 * https://playground.babylonjs.com/#0NM5C0#11
 *
 *
 */
export class Particles {
  starTexture: Texture;
  starTexture2: Texture;
  starTexture3: Texture;
  starTexture4: Texture;

  constructor(public level: Level) {
    this.starTexture = new Texture('assets/particles/star_09.png', this.level.scene);
    this.starTexture2 = new Texture('assets/particles/star_08.png', this.level.scene);
    this.starTexture3 = new Texture('assets/particles/star_01.png', this.level.scene);
    this.starTexture4 = new Texture('assets/particles/star_04.png', this.level.scene);
  }

  static createParticleSystem(
    name: string,
    capacity: number,
    level: Level | Game,
    allowGPU: boolean = true
  ): ParticleSystem | GPUParticleSystem {
    let particleSystem: ParticleSystem | GPUParticleSystem;
    if (allowGPU && GPUParticleSystem.IsSupported) {
      particleSystem = new GPUParticleSystem(name, { capacity: capacity }, level.scene);
      particleSystem.activeParticleCount = capacity;
    } else {
      particleSystem = new ParticleSystem(name, capacity, level.scene);
    }
    return particleSystem;
  }

  async createExplosionParticleSystem(emitter?: Vector3 | AbstractMesh, scale: number = 1): Promise<ParticleSystemSet> {
    const particleSet = await ParticleHelper.CreateAsync('explosion', this.level.scene);
    if (emitter) {
      if (emitter instanceof Vector3) {
        particleSet.emitterNode = emitter;
      } else {
        particleSet.emitterNode = emitter.position;
      }
    }

    if (scale !== 1) {
      for (const sys of particleSet.systems) {
        sys.minSize *= scale;
        sys.maxSize *= scale;
        /* sys.minLifeTime *= scale;
        sys.maxLifeTime *= scale;
        sys.minEmitPower *= scale;
        sys.maxEmitPower *= scale; */
      }
    }
    return particleSet;
  }

  /**
   * Helper to run a particle system once and dispose it when all particles are dead.
   * This is more robust than relying on targetStopDuration which can be frame-rate dependent.
   */
  oneShot(particleSystem: ParticleSystem | GPUParticleSystem): void {
    particleSystem.manualEmitCount = particleSystem.getCapacity();
    particleSystem.emitRate = 0;
    particleSystem.disposeOnStop = true;
    particleSystem.start();

    // Use a simple timeout to ensure the system is disposed.
    // This decouples the disposal from frame-rate dependent simulation loops,
    // solving both the "multiple emits" and "slow down" issues.
    const duration = particleSystem.maxLifeTime + 0.5;
    setTimeout(() => {
      particleSystem.stop();
      particleSystem.dispose();
    }, duration * 1000);
  }

  async createSunParticleSystem(emitter: Vector3 | AbstractMesh): Promise<ParticleSystemSet> {
    const particleSet = await ParticleHelper.CreateAsync('sun', this.level.scene);
    particleSet.emitterNode = emitter;
    particleSet.setEmitterAsSphere({ diameter: 20, segments: 20, color: Color3.White() }, 0, this.level.scene);
    return particleSet;
  }

  async createPresetRainParticleSystem(emitRateFactor: number = 1, autoStart: boolean = true): Promise<ParticleSystemSet> {
    const maxEmitRate = 4000;
    const emitRate = maxEmitRate * emitRateFactor;

    const particleSet = await ParticleHelper.CreateAsync('rain', this.level.scene);
    particleSet.emitterNode = this.level.game.scene.activeCamera as any;

    for (const sys of particleSet.systems) {
      (sys as any).minEmitBox = new Vector3(-40, 10, -40);
      (sys as any).maxEmitBox = new Vector3(40, 50, 40);
      sys.emitRate = emitRate;
    }

    if (autoStart) {
      particleSet.start();
    }
    return particleSet;
  }

  createSnowParticleSystem(emitRateFactor: number = 1, autoStart: boolean = true): ParticleSystem | GPUParticleSystem {
    const maxEmitRate = 20000;
    const capacity = 50000; // Very high capacity for GPU
    const emitRate = maxEmitRate * emitRateFactor;

    const particleSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('snow', capacity, this.level);

    // Texture
    particleSystem.particleTexture = new Texture('assets/particles/snowflake1.png', this.level.scene);

    // Emitter
    particleSystem.emitter = this.level.game.scene.activeCamera as any;
    particleSystem.minEmitBox = new Vector3(-40, 10, 0);
    particleSystem.maxEmitBox = new Vector3(40, 50, 100);

    // Color
    particleSystem.color1 = new Color4(1.0, 1.0, 1.0, 1.0);
    particleSystem.color2 = new Color4(0.95, 0.95, 0.95, 1.0);
    particleSystem.colorDead = new Color4(0.9, 0.9, 0.9, 0.0);

    // Size
    particleSystem.minSize = 0.1;
    particleSystem.maxSize = 0.5;

    // Lifetime
    particleSystem.minLifeTime = 1.6;
    particleSystem.maxLifeTime = 3.5;

    // Emission
    particleSystem.emitRate = emitRate;

    // Gravity/Direction
    particleSystem.gravity = new Vector3(0, -2, 0);
    particleSystem.direction1 = new Vector3(-2, -5, -2);
    particleSystem.direction2 = new Vector3(2, -8, 2);

    // Physics
    particleSystem.minEmitPower = 1;
    particleSystem.maxEmitPower = 3;
    particleSystem.updateSpeed = 0.005;

    // Appearance
    particleSystem.billboardMode = ParticleSystem.BILLBOARDMODE_ALL;
    particleSystem.blendMode = ParticleSystem.BLENDMODE_STANDARD;

    if (!GPUParticleSystem.IsSupported) {
      const noiseTexture = new NoiseProceduralTexture('perlin', 256, this.level.game.scene);
      (particleSystem as ParticleSystem).noiseTexture = noiseTexture;
      (particleSystem as ParticleSystem).noiseStrength = new Vector3(5, 5, 5);
    }

    if (autoStart) {
      particleSystem.start();
    }

    return particleSystem;
  }

  createRainParticleSystem(emitRateFactor: number = 1, autoStart: boolean = true): ParticleSystem | GPUParticleSystem {
    const maxEmitRate = 4000;
    const emitRate = maxEmitRate * emitRateFactor;

    const particleSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('rail', maxEmitRate, this.level);

    // Texture
    particleSystem.particleTexture = new Texture('assets/particles/rain.png', this.level.scene);

    // Emitter
    particleSystem.emitter = this.level.game.scene.activeCamera as any;
    particleSystem.minEmitBox = new Vector3(-40, 10, 0);
    particleSystem.maxEmitBox = new Vector3(40, 50, 100);

    // Color
    particleSystem.color1 = new Color4(0.4, 0.6, 1.0, 1.0);
    particleSystem.color2 = new Color4(0.5, 0.7, 1.0, 1.0);
    particleSystem.colorDead = new Color4(0.6, 0.8, 1.0, 0.0);

    // Size
    particleSystem.minSize = 0.2;
    particleSystem.maxSize = 0.3;

    // Lifetime
    particleSystem.minLifeTime = 1.0;
    particleSystem.maxLifeTime = 1.5;

    // Emission
    particleSystem.emitRate = emitRate;

    // Gravity/Direction
    particleSystem.gravity = new Vector3(0, -50, 0);
    particleSystem.direction1 = new Vector3(0, -1, 0);
    particleSystem.direction2 = new Vector3(0, -1, 0);

    // Physics
    particleSystem.minEmitPower = 20;
    particleSystem.maxEmitPower = 30;
    particleSystem.updateSpeed = 0.01;

    // Appearance
    particleSystem.billboardMode = ParticleSystem.BILLBOARDMODE_ALL;
    particleSystem.blendMode = ParticleSystem.BLENDMODE_ADD;

    const noiseTexture = new NoiseProceduralTexture('perlin', 256, this.level.game.scene);

    particleSystem.noiseTexture = noiseTexture;
    particleSystem.noiseStrength = new Vector3(2, 2, 2);

    if (autoStart) {
      particleSystem.start();
    }

    return particleSystem;
  }

  createHeavyRainParticleSystem(emitRateFactor: number = 1, autoStart: boolean = true): ParticleSystem | GPUParticleSystem {
    const maxEmitRate = 6000;
    const emitRate = maxEmitRate * emitRateFactor;

    const particleSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('rail', maxEmitRate, this.level);

    // Texture
    particleSystem.particleTexture = new Texture('assets/particles/rain.png', this.level.scene);

    // Emitter
    particleSystem.emitter = this.level.game.scene.activeCamera as any;
    particleSystem.minEmitBox = new Vector3(-40, 10, 0);
    particleSystem.maxEmitBox = new Vector3(40, 50, 100);

    // Color
    particleSystem.color1 = new Color4(0.4, 0.6, 1.0, 1.0);
    particleSystem.color2 = new Color4(0.5, 0.7, 1.0, 1.0);
    particleSystem.colorDead = new Color4(0.6, 0.8, 1.0, 0.0);

    particleSystem.minScaleY = 8;
    particleSystem.maxScaleY = 12;

    particleSystem.minScaleX = 0.2;
    particleSystem.maxScaleX = 0.5;

    // Size
    particleSystem.minSize = 0.2;
    particleSystem.maxSize = 0.3;

    // Lifetime
    particleSystem.minLifeTime = 1.0;
    particleSystem.maxLifeTime = 1.5;

    // Emission
    particleSystem.emitRate = emitRate;

    // Gravity/Direction
    particleSystem.gravity = new Vector3(0, -75, 0);
    particleSystem.direction1 = new Vector3(0, -1, 0);
    particleSystem.direction2 = new Vector3(0, -1, 0);

    // Physics
    particleSystem.minEmitPower = 25;
    particleSystem.maxEmitPower = 30;
    particleSystem.updateSpeed = 0.01;

    // Appearance
    particleSystem.billboardMode = ParticleSystem.BILLBOARDMODE_ALL;
    particleSystem.blendMode = ParticleSystem.BLENDMODE_ADD;

    const noiseTexture = new NoiseProceduralTexture('perlin', 256, this.level.game.scene);

    particleSystem.noiseTexture = noiseTexture;
    particleSystem.noiseStrength = new Vector3(2, 2, 2);

    if (autoStart) {
      particleSystem.start();
    }

    return particleSystem;
  }

  createGroundFogParticleSystem(emitRateFactor: number = 1, autoStart: boolean = true): ParticleSystem {
    const capacity = 20000 * emitRateFactor;
    const wrapRadius = 42; // Partikel beyond this distance are teleported to the camera

    // CPU-ParticleSystem: enables custom updateFunction for wrap-around
    const particleSystem = new ParticleSystem('groundFog', capacity, this.level.scene);

    particleSystem.particleTexture = new Texture('assets/particles/smoke_04.png', this.level.scene);

    // Emitter-Vector3, updated every frame to camera position.
    // Particles are created in world coordinates near the camera.
    const camera = this.level.game.scene.activeCamera;
    const emitterPos = camera.position.clone();
    particleSystem.emitter = emitterPos;

    const observer = this.level.scene.onBeforeRenderObservable.add(() => {
      emitterPos.copyFromFloats(camera.position.x, camera.position.y, camera.position.z);
    });
    particleSystem.onDisposeObservable.addOnce(() => {
      this.level.scene.onBeforeRenderObservable.remove(observer);
    });

    particleSystem.minEmitBox = new Vector3(-wrapRadius, -6, -wrapRadius);
    particleSystem.maxEmitBox = new Vector3(wrapRadius, 0, wrapRadius);

    particleSystem.color1 = new Color4(0.8, 0.8, 0.8, 0.1);
    particleSystem.color2 = new Color4(0.95, 0.95, 0.95, 0.15);
    particleSystem.colorDead = new Color4(0.9, 0.9, 0.9, 0.0);
    particleSystem.minSize = 3.5;
    particleSystem.maxSize = 5.0;

    // Particles live forever - no waiting, no refilling.
    // The wrap-around takes care of the "recycling".
    particleSystem.minLifeTime = Number.MAX_SAFE_INTEGER;
    particleSystem.emitRate = 0; // No continuous emission needed
    particleSystem.manualEmitCount = capacity; // Immediately fully filled

    particleSystem.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    particleSystem.gravity = new Vector3(0, 0, 0);
    particleSystem.direction1 = new Vector3(0, 0, 0);
    particleSystem.direction2 = new Vector3(0, 0, 0);
    particleSystem.minAngularSpeed = -0.3;
    particleSystem.maxAngularSpeed = 0.3;
    particleSystem.minEmitPower = 0;
    particleSystem.maxEmitPower = 0;
    particleSystem.updateSpeed = 0.016;

    const ground = this.level.ground;

    // Wrap-Around: Particles that are too far from the camera
    // are immediately repositioned near the current camera position.
    // This ensures that the full fog density follows the player - without waiting time.
    particleSystem.updateFunction = function (particles: any[]) {
      const dt = (this as any)._scaledUpdateSpeed;
      const cx = emitterPos.x;
      const cy = emitterPos.y;
      const cz = emitterPos.z;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.age += dt;

        if (p.age >= p.lifeTime) {
          (this as any).recycleParticle(p);
          i--;
          continue;
        }

        const dx = p.position.x - cx;
        const dz = p.position.z - cz;

        if (Math.abs(dx) > wrapRadius || Math.abs(dz) > wrapRadius) {
          // Teleport particle to camera, redistribute over area
          p.position.x = cx + (Math.random() * 2 - 1) * wrapRadius;
          //p.position.y = cy - 2 + Math.random() * 2.5;
          p.position.y = ground.getHeightAtCoordinates(p.position.x, p.position.z);
          p.position.z = cz + (Math.random() * 2 - 1) * wrapRadius;
        }

        // Slow rotation of the fog wisps
        p.angle += p.angularSpeed * dt;
      }
    };

    if (autoStart) {
      particleSystem.start();
    }

    return particleSystem;
  }

  createMovementParticleSystem(emitter: AbstractMesh): ParticleSystem | GPUParticleSystem {
    const particleSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('movementParticles', 100, this.level);

    particleSystem.particleTexture = new Texture('assets/particles/smoke2.png', this.level.scene);
    particleSystem.emitter = emitter;

    // Create a sphere emitter to disperse particles from the feet/base
    // We'll set the emitter position offset in Player.ts or rely on mesh position (which is usually feet for characters)
    // But Player mesh origin corresponds to center usually? No, line 141: rootMesh.position = new Vector3(0, -this.playerHeight / 2, 0);
    // The emitter passed is `displayMesh`, which is rootMesh. So its position is at player feet relative to parent.

    particleSystem.createSphereEmitter(0.5);

    // Blast downwards
    particleSystem.direction1 = new Vector3(-1, -5, -1);
    particleSystem.direction2 = new Vector3(1, -8, 1);

    particleSystem.gravity = new Vector3(0, 2, 0); // Particles float up slightly after blast

    particleSystem.minSize = 0.5;
    particleSystem.maxSize = 1.0;

    particleSystem.minLifeTime = 0.5;
    particleSystem.maxLifeTime = 1.0;

    particleSystem.emitRate = 20;

    particleSystem.blendMode = ParticleSystem.BLENDMODE_STANDARD;

    // Color: White/Grey smoke fade out
    particleSystem.color1 = new Color4(0.6, 0.6, 0.6, 0.2);
    particleSystem.color2 = new Color4(0.8, 0.8, 0.8, 0.2);
    particleSystem.colorDead = new Color4(0.3, 0.3, 0.3, 0.0);

    particleSystem.minEmitPower = 2;
    particleSystem.maxEmitPower = 5;
    particleSystem.updateSpeed = 0.02;

    return particleSystem;
  }

  createImpactDustParticleSystem(emitter: AbstractMesh): ParticleSystem | GPUParticleSystem {
    // Force CPU particles for consistent burst
    const particleSystem = Particles.createParticleSystem('impactDust', 200, this.level);

    particleSystem.particleTexture = new Texture('assets/particles/smoke2.png', this.level.scene);
    particleSystem.emitter = emitter;

    // Create a sphere emitter to disperse particles from the impact point
    // Emit from a slightly larger area
    particleSystem.createSphereEmitter(1.5);

    // Blast upwards and outwards
    particleSystem.direction1 = new Vector3(-2, 2, -2);
    particleSystem.direction2 = new Vector3(2, 5, 2);

    particleSystem.gravity = new Vector3(0, 0, 0); // Dust floats

    particleSystem.minSize = 1.0;
    particleSystem.maxSize = 2.5;

    particleSystem.minLifeTime = 1.0;
    particleSystem.maxLifeTime = 2.0;

    // Burst
    particleSystem.manualEmitCount = 200;
    particleSystem.emitRate = 0;

    particleSystem.blendMode = ParticleSystem.BLENDMODE_STANDARD;

    // Color: Brown/Grey dust
    particleSystem.color1 = new Color4(0.6, 0.5, 0.4, 0.75);
    particleSystem.color2 = new Color4(0.5, 0.5, 0.5, 0.5);
    particleSystem.colorDead = new Color4(0.4, 0.4, 0.4, 0.0);

    particleSystem.minEmitPower = 2;
    particleSystem.maxEmitPower = 6;
    particleSystem.updateSpeed = 0.01;

    particleSystem.disposeOnStop = true;
    particleSystem.targetStopDuration = 0.25;

    return particleSystem;
  }

  createAirJumpParticleSystem(emitter: AbstractMesh): ParticleSystem | GPUParticleSystem {
    // Force CPU particles for reliable burst behavior with manualEmitCount
    const particleSystem = Particles.createParticleSystem('airJumpParticles', 100, this.level, false);

    particleSystem.particleTexture = new Texture('assets/particles/smoke2.png', this.level.scene);
    particleSystem.emitter = emitter;

    particleSystem.createSphereEmitter(0.5);

    // Blast downwards
    particleSystem.direction1 = new Vector3(-1, -5, -1);
    particleSystem.direction2 = new Vector3(1, -8, 1);

    particleSystem.gravity = new Vector3(0, 2, 0); // Particles float up slightly after blast

    particleSystem.minSize = 0.5;
    particleSystem.maxSize = 1.0;

    particleSystem.minLifeTime = 0.5;
    particleSystem.maxLifeTime = 1.0;

    // manualEmitCount > -1 means "emit this many then stop".
    particleSystem.emitRate = 0;
    particleSystem.manualEmitCount = 100;

    particleSystem.blendMode = ParticleSystem.BLENDMODE_STANDARD;

    // Color: White/Grey smoke fade out
    particleSystem.color1 = new Color4(0.8, 0.8, 0.8, 0.3);
    particleSystem.color2 = new Color4(0.9, 0.9, 0.9, 0.3);
    particleSystem.colorDead = new Color4(1, 1, 1, 0.0);

    particleSystem.minEmitPower = 2;
    particleSystem.maxEmitPower = 5;
    particleSystem.updateSpeed = 0.02;

    particleSystem.disposeOnStop = true;
    particleSystem.targetStopDuration = 0.5;

    return particleSystem;
  }

  createWindSystem(emitter: AbstractMesh | Vector3, windDirection: Vector3): ParticleSystem | GPUParticleSystem {
    const particleSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('windParticles', 600, this.level);

    particleSystem.particleTexture = new Texture('assets/particles/line.png', this.level.scene);
    particleSystem.emitter = emitter;

    // Emit across a wide area around the obstacle so wind is visually obvious
    particleSystem.minEmitBox = new Vector3(-8, -0.5, -8);
    particleSystem.maxEmitBox = new Vector3(8, 4, 8);
    particleSystem.billboardMode = ParticleSystem.BILLBOARDMODE_Y;
    particleSystem.blendMode = ParticleSystem.BLENDMODE_ONEONE;

    particleSystem.direction1 = windDirection.scale(8);
    particleSystem.direction2 = windDirection.scale(18);
    particleSystem.minLifeTime = 0.8;
    particleSystem.maxLifeTime = 1.8;
    particleSystem.emitRate = 80;

    particleSystem.color1 = new Color4(0.7, 0.85, 1, 0.6);
    particleSystem.color2 = new Color4(0.9, 0.95, 1, 0.3);
    particleSystem.colorDead = new Color4(1, 1, 1, 0);

    particleSystem.minSize = 1;
    particleSystem.maxSize = 1.5;
    particleSystem.minEmitPower = 1;
    particleSystem.maxEmitPower = 2;
    particleSystem.updateSpeed = 0.01;

    particleSystem.start();
    return particleSystem;
  }

  createGlitterParticleSystem(
    emitter: AbstractMesh | Vector3,
    autoStart: boolean = true,
    color1: Color4 = new Color4(0.16, 0.65, 0.93),
    color2: Color4 = new Color4(0.91, 0.04, 0.45),
    colorDead: Color4 = new Color4(0.04, 0.93, 0.4, 0.3)
  ): ParticleSystem | GPUParticleSystem {
    // Create a particle system
    const particleSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('glitter', 2000, this.level);

    //Texture of each particle
    particleSystem.particleTexture = this.level.game.materialService.createTexture('assets/particles/fire.jpg');

    // Where the particles come from
    particleSystem.emitter = emitter; // the starting object, the emitter
    particleSystem.minEmitBox = new Vector3(0, 0, 0); // Starting all from
    particleSystem.maxEmitBox = new Vector3(1, 0, 0); // To...

    // Colors of all particles
    particleSystem.color1 = color1;
    particleSystem.color2 = color2;
    particleSystem.colorDead = colorDead;

    // Size of each particle (random between...
    particleSystem.minSize = 1.5;
    particleSystem.maxSize = 2.9;

    // Life time of each particle (random between...
    particleSystem.minLifeTime = 2.3;
    particleSystem.maxLifeTime = 8.5;

    // Emission rate
    particleSystem.emitRate = 1150;

    // Blend mode : BLENDMODE_ONEONE, or BLENDMODE_STANDARD
    particleSystem.blendMode = ParticleSystem.BLENDMODE_ONEONE;

    // Set the gravity of all particles
    particleSystem.gravity = new Vector3(0, -0.01, 0);

    // Direction of each particle after it has been emitted

    particleSystem.direction1 = new Vector3(
      this.level.integerInRange(-2, 2),
      this.level.integerInRange(-2, 10),
      this.level.integerInRange(-2, 2)
    );
    particleSystem.direction2 = new Vector3(
      this.level.integerInRange(-2, 2),
      this.level.integerInRange(-2, 10),
      this.level.integerInRange(-2, 2)
    );

    // Angular speed, in radians
    particleSystem.minAngularSpeed = 0;
    particleSystem.maxAngularSpeed = Math.PI;

    // Speed
    particleSystem.minEmitPower = 1;
    particleSystem.maxEmitPower = 0.4;
    particleSystem.updateSpeed = 0.009;

    if (autoStart) {
      // Start the particle system
      particleSystem.start();
    }

    return particleSystem;
  }

  createConfettiParticleSystem(
    emitter: AbstractMesh | Vector3,
    duration: number,
    autoStart: boolean = true
  ): ParticleSystem | GPUParticleSystem {
    const particleSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('confetti', 100, this.level);

    particleSystem.particleTexture = new Texture('assets/particles/solidStar.png', this.level.scene);
    particleSystem.emitter = emitter;
    particleSystem.minSize = 0.5;
    particleSystem.maxSize = 1;
    particleSystem.emitRate = 100;
    particleSystem.targetStopDuration = duration;
    particleSystem.minLifeTime = 3;
    particleSystem.maxLifeTime = 14;
    particleSystem.particleEmitterType = new ConeParticleEmitter(1, Math.PI / 2);
    particleSystem.minAngularSpeed = 0.03;
    particleSystem.minEmitPower = 5;
    particleSystem.maxEmitPower = 10;
    particleSystem.addColorGradient(0, new Color4(0, 0, 0, 1), new Color4(0, 0, 0, 1));
    particleSystem.addColorGradient(1, new Color4(1, 1, 1, 1), new Color4(1, 1, 1, 1));
    particleSystem.getColorGradients()[1].color1 = Color4.FromColor3(Color3.Yellow());
    particleSystem.getColorGradients()[1].color2 = Color4.FromColor3(Color3.Purple());
    particleSystem.getColorGradients()[1].gradient = 0.5;
    particleSystem.getColorGradients().push(new ColorGradient(1, new Color4(1, 1, 1, 1), new Color4(1, 1, 1, 1)));
    particleSystem.getColorGradients()[2].color1 = Color4.FromColor3(Color3.Green());
    particleSystem.getColorGradients()[2].color2 = Color4.FromColor3(Color3.Blue());
    particleSystem.minInitialRotation = 45;
    particleSystem.maxInitialRotation = 360;
    particleSystem.getColorGradients()[0].color1 = Color4.FromColor3(Color3.Teal());
    particleSystem.getColorGradients()[0].color2 = Color4.FromColor3(Color3.Red());
    particleSystem.gravity = new Vector3(0, -6, 0);
    particleSystem.addDragGradient(0, 0.4);
    particleSystem.addDragGradient(1.0, 1);
    particleSystem.updateSpeed = 0.021;
    if (autoStart) {
      particleSystem.start();
    }
    return particleSystem;
  }

  createFogParticleSystem(
    emitter: AbstractMesh | Vector3,
    color: Color3 = new Color3(0.95, 0.54, 0.41),
    minEmitBox: Vector3 = new Vector3(-75, -75, -75),
    maxEmitBox: Vector3 = new Vector3(75, 75, 75)
  ): ParticleSystem | GPUParticleSystem {
    const particleSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('particles', 5000, this.level);

    particleSystem.manualEmitCount = particleSystem.getCapacity();
    particleSystem.minEmitBox = minEmitBox;
    particleSystem.maxEmitBox = maxEmitBox;

    particleSystem.particleTexture = new Texture('assets/particles/smoke2.png', this.level.scene);
    particleSystem.emitter = emitter;

    particleSystem.color1 = Color4.FromColor3(color, 0.7);
    particleSystem.color2 = new Color4(0.95, 0.95, 0.95, 0.35);
    particleSystem.colorDead = new Color4(0.9, 0.9, 0.9, 0.6);
    particleSystem.minSize = 6.5;
    particleSystem.maxSize = 22.0;
    particleSystem.minLifeTime = Number.MAX_SAFE_INTEGER;
    particleSystem.emitRate = 5000;
    particleSystem.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    particleSystem.gravity = new Vector3(0, 0, 0);
    particleSystem.direction1 = new Vector3(0.1, 0, 0);
    particleSystem.direction2 = new Vector3(0.1, 0.4, 0);
    particleSystem.minAngularSpeed = -2;
    particleSystem.maxAngularSpeed = 1;
    particleSystem.minEmitPower = 0.5;
    particleSystem.maxEmitPower = 2;
    particleSystem.updateSpeed = 0.005;
    return particleSystem;
  }

  createFireworksRisingParticleSystem(emitter: AbstractMesh | Vector3, duration: number, autoStart: boolean = true): ParticleSystem {
    // Create sub emitters
    const subEmitter2 = new SubEmitter(new ParticleSystem('particles', 50, this.level.scene));
    subEmitter2.particleSystem.particleTexture = new Texture('assets/particles/flare.png', this.level.scene);
    subEmitter2.particleSystem.emitter = emitter;
    subEmitter2.particleSystem.maxEmitBox = new Vector3(0, 0, 0);
    subEmitter2.particleSystem.minEmitBox = new Vector3(0, 0, 0);
    subEmitter2.particleSystem.minEmitPower = -10;
    subEmitter2.particleSystem.maxEmitPower = -10;
    subEmitter2.particleSystem.direction1 = new Vector3(0, 1, 0);
    subEmitter2.particleSystem.direction2 = new Vector3(0, 1, 0);
    subEmitter2.particleSystem.minSize = 0.04;
    subEmitter2.particleSystem.maxSize = 0.06;
    subEmitter2.particleSystem.minLifeTime = 0.8;
    subEmitter2.particleSystem.maxLifeTime = 1.0;
    subEmitter2.particleSystem.emitRate = 60;
    subEmitter2.particleSystem.addColorGradient(0, new Color4(0.97, 0.92, 0.73));
    subEmitter2.particleSystem.addColorGradient(0.9, new Color4(0.97, 0.92, 0.73, 0.87));
    subEmitter2.particleSystem.addColorGradient(1.0, new Color4(1, 1, 1, 0.87));
    subEmitter2.particleSystem.addDragGradient(0, 1.0);
    subEmitter2.particleSystem.addDragGradient(1.0, 1.0);
    subEmitter2.particleSystem.gravity = new Vector3(0, -9.81, 0);
    subEmitter2.type = SubEmitterType.ATTACHED;
    subEmitter2.inheritDirection = true;

    // Create sub emitters
    const subEmitter1 = new SubEmitter(new ParticleSystem('particles', 50, this.level.scene));
    subEmitter1.particleSystem.particleTexture = new Texture('assets/particles/flare.png', this.level.scene);
    subEmitter1.particleSystem.emitter = emitter;
    subEmitter1.particleSystem.maxEmitBox = new Vector3(0, 0, 0);
    subEmitter1.particleSystem.minEmitBox = new Vector3(0, 0, 0);
    subEmitter1.particleSystem.minEmitPower = -10;
    subEmitter1.particleSystem.maxEmitPower = -10;
    subEmitter1.particleSystem.direction1 = new Vector3(0, 1, 0);
    subEmitter1.particleSystem.direction2 = new Vector3(0, 1, 0);
    subEmitter1.particleSystem.minSize = 0.02;
    subEmitter1.particleSystem.maxSize = 0.04;
    subEmitter1.particleSystem.minLifeTime = 1.0;
    subEmitter1.particleSystem.maxLifeTime = 1.2;
    subEmitter1.particleSystem.emitRate = 60;
    subEmitter1.particleSystem.addColorGradient(0, new Color4(0.97, 0.92, 0.73));
    subEmitter1.particleSystem.addColorGradient(0.9, new Color4(0.97, 0.92, 0.73, 0.87));
    subEmitter1.particleSystem.addColorGradient(1.0, new Color4(1, 1, 1, 0.87));
    subEmitter1.particleSystem.addDragGradient(0, 1.0);
    subEmitter1.particleSystem.addDragGradient(1.0, 1.0);
    subEmitter1.particleSystem.gravity = new Vector3(0, -9.81, 0);
    subEmitter1.type = SubEmitterType.ATTACHED;
    subEmitter1.inheritDirection = true;

    const createFireworkSubEmitter = (color, type, name) => {
      const particleSystem1 = new ParticleSystem(name, 2000, this.level.scene);
      particleSystem1.emitter = emitter;
      //Texture of each particle
      particleSystem1.particleTexture = new Texture('assets/particles/flare.png', this.level.scene);
      if (type === 0) {
        particleSystem1.createSphereEmitter(2);
      } else if (type === 1) {
        particleSystem1.createSphereEmitter(2);
      }

      const createFireworkSubEmitter = (color, type, name) => {
        const particleSystem1 = new ParticleSystem(name, 2000, this.level.scene);
        particleSystem1.emitter = emitter;
        //Texture of each particle
        particleSystem1.particleTexture = new Texture('assets/particles/flare.png', this.level.scene);
        if (type === 0) {
          particleSystem1.createSphereEmitter(2);
        } else if (type === 1) {
          particleSystem1.createSphereEmitter(2);
        }
      };
      // Colors of all particles
      particleSystem1.color1 = color;
      particleSystem1.color2 = color;
      particleSystem1.colorDead = new Color4(0.31, 0.69, 0.99);
      particleSystem1.subEmitters = [];
      particleSystem1.minSize = 0.05;
      particleSystem1.maxSize = 0.05;
      particleSystem1.addSizeGradient(0, 0.1);
      particleSystem1.addSizeGradient(0.8, 0.3);
      particleSystem1.addSizeGradient(1.0, 0.1);
      particleSystem1.minScaleY = 1.2;
      particleSystem1.maxScaleY = 1.5;
      particleSystem1.minLifeTime = 0.9;
      particleSystem1.maxLifeTime = 1.0;
      particleSystem1.manualEmitCount = 50;
      particleSystem1.gravity = new Vector3(0, -9.81, 0);
      particleSystem1.addDragGradient(0.0, 0);
      particleSystem1.addDragGradient(0.25, 0.2);
      particleSystem1.addDragGradient(1.0, 0.2);
      particleSystem1.createSphereEmitter(1, 1);
      particleSystem1.disposeOnStop = true;
      particleSystem1.blendMode = ParticleSystem.BLENDMODE_ONEONE;
      particleSystem1.minEmitPower = 3;
      particleSystem1.maxEmitPower = 3;
      particleSystem1.updateSpeed = 0.008;
      var se = new SubEmitter(particleSystem1);
      se.inheritDirection = true;
      return se;
    };

    // Create a particle system
    const particleSystem = new ParticleSystem('particles', 2000, this.level.scene);

    //Texture of each particle
    particleSystem.particleTexture = new Texture('assets/particles/flare.png', this.level.scene);

    // Where the particles come from
    particleSystem.emitter = new Vector3(0, 0, 0); // the starting object, the emitter
    particleSystem.createConeEmitter(1.2);

    // Colors of all particles
    particleSystem.addColorGradient(0, new Color4(0.31, 0.69, 0.99, 1.0));
    particleSystem.addColorGradient(0.5, new Color4(0.31, 0.69, 0.99, 0.5));
    particleSystem.addColorGradient(0.95, new Color4(0.95, 0.97, 1));
    particleSystem.addColorGradient(1.0, new Color4(0.97, 0.92, 0.73));
    particleSystem.subEmitters = [[subEmitter1, subEmitter2, createFireworkSubEmitter(new Color4(0.98, 1, 0), 1, 'first')]];
    particleSystem.minSize = 0.1;
    particleSystem.maxSize = 0.1;
    particleSystem.addSizeGradient(0, 0.1);
    particleSystem.addSizeGradient(0.8, 0.1);
    particleSystem.addSizeGradient(1.0, 0.4);
    particleSystem.minLifeTime = 0.6;
    particleSystem.maxLifeTime = 0.8;
    particleSystem.emitRate = 2;
    particleSystem.manualEmitCount = 1;
    particleSystem.targetStopDuration = duration;
    particleSystem.blendMode = ParticleSystem.BLENDMODE_ONEONE;
    particleSystem.minAngularSpeed = 0;
    particleSystem.maxAngularSpeed = Math.PI;
    particleSystem.minEmitPower = 12;
    particleSystem.maxEmitPower = 15;
    particleSystem.updateSpeed = 0.008;
    particleSystem.gravity = new Vector3(0, -9.81, 0);
    particleSystem.direction1 = new Vector3(-1, 0, 0);
    particleSystem.direction2 = new Vector3(1, 0, 0);
    if (autoStart) {
      particleSystem.start();
    }
    return particleSystem;
  }

  /**
   * Creates a particle effect that visualizes the disappearance of a mesh.
   * A fresh CPU ParticleSystem is created per call (fast for 50 particles) and disposed
   * after the effect ends. The particle texture is shared (not cloned) and dispose(false)
   * prevents it from being released when the system is disposed.
   * @param mesh The disappearing mesh
   * @param duration Duration of the effect in seconds
   * @param color Main color of the effect (optional)
   * @param autoStart Start automatically
   * @returns The created ParticleSystem
   */
  createDisappearanceParticleSystem(mesh: AbstractMesh, duration: number = 0.4, color?: Color4, autoStart: boolean = true): ParticleSystem {
    // CPU-only: 50 particle CPU system allocates in < 0.5ms and avoids GPU-pool state issues.
    const particleSystem = new ParticleSystem('disappearance', 50, this.level.scene);

    // Shared texture reference – no clone, no GPU upload.
    // disposeOnStop = false + manual dispose(false) ensures the shared texture is never released.
    particleSystem.particleTexture = this.starTexture;
    particleSystem.disposeOnStop = false;

    // Adjust emitter to mesh bounding box in world space.
    const boundingInfo = mesh.getBoundingInfo();
    const size = boundingInfo.boundingBox.extendSizeWorld;
    const c = boundingInfo.boundingBox.centerWorld;

    particleSystem.createBoxEmitter(
      new Vector3(-size.x, -size.y, -size.z),
      new Vector3(size.x, size.y, size.z),
      new Vector3(c.x - size.x, c.y - size.y, c.z - size.z),
      new Vector3(c.x + size.x, c.y + size.y, c.z + size.z)
    );

    // Colors
    const mainColor = color ?? new Color4(0.8, 0.5, 1.0, 1.0);
    particleSystem.addColorGradient(0, mainColor);
    particleSystem.addColorGradient(0.5, new Color4(mainColor.r, mainColor.g, mainColor.b, 0.5));
    particleSystem.addColorGradient(1.0, new Color4(1, 1, 1, 0));

    particleSystem.addSizeGradient(0, 0.8);
    particleSystem.addSizeGradient(0.3, 0.6);
    particleSystem.addSizeGradient(1.0, 0.0);

    particleSystem.minSize = 0.2;
    particleSystem.maxSize = 0.6;
    particleSystem.minLifeTime = duration;
    particleSystem.maxLifeTime = duration;
    particleSystem.manualEmitCount = 50;
    particleSystem.emitRate = 0;
    particleSystem.blendMode = ParticleSystem.BLENDMODE_ADD;
    particleSystem.minAngularSpeed = 0;
    particleSystem.maxAngularSpeed = Math.PI;
    particleSystem.minEmitPower = 0.5;
    particleSystem.maxEmitPower = 2;
    particleSystem.updateSpeed = 0.01;
    particleSystem.gravity = new Vector3(0, 0.5, 0);
    particleSystem.addDragGradient(0, 0.7);
    particleSystem.addDragGradient(1.0, 0.3);

    if (autoStart) {
      particleSystem.start();
      // Dispose after all particles have faded – pass false to keep the shared texture alive.
      setTimeout(
        () => {
          particleSystem.stop();
          particleSystem.dispose(false);
        },
        (duration + 0.15) * 1000
      );
    }

    return particleSystem;
  }

  /**
   * Creates a ring of energy particles that orbit around the emitter
   * Used for invincibility shield visualization
   * @param emitter The mesh to emit particles around
   * @returns The created ParticleSystem
   */
  createShieldParticleRing(emitter: AbstractMesh): ParticleSystem | GPUParticleSystem {
    const particleSystem = Particles.createParticleSystem('shieldRing', 150, this.level);

    // Star/flare texture for energy effect
    particleSystem.particleTexture = new Texture('assets/particles/flare.png', this.level.scene);
    particleSystem.emitter = emitter;

    // Emit from the surface of a sphere to surround the shield mesh
    // The shield is diameter 2.5, so radius ~1.25 - emit slightly inside so particles appear on surface
    particleSystem.createSphereEmitter(1.2, 0); // radius, radiusRange (0 = only on surface)

    // Cyan/white energy colors matching the shield
    particleSystem.color1 = new Color4(0.3, 0.8, 1.0, 1.0);
    particleSystem.color2 = new Color4(0.6, 0.9, 1.0, 0.8);
    particleSystem.colorDead = new Color4(1.0, 1.0, 1.0, 0.0);

    // Small glowing particles
    particleSystem.minSize = 0.1;
    particleSystem.maxSize = 0.25;

    // Size gradient - grow then shrink
    particleSystem.addSizeGradient(0, 0.05);
    particleSystem.addSizeGradient(0.3, 0.2);
    particleSystem.addSizeGradient(1.0, 0.0);

    // Lifespan - shorter for more dynamic effect
    particleSystem.minLifeTime = 0.4;
    particleSystem.maxLifeTime = 0.8;

    // Higher emission for better coverage around the sphere
    particleSystem.emitRate = 80;

    // Additive blending for glow effect
    particleSystem.blendMode = ParticleSystem.BLENDMODE_ADD;

    // Particles move outward from sphere surface (radial direction is automatic with sphere emitter)
    // No gravity - particles should float around the shield
    particleSystem.gravity = new Vector3(0, 0, 0);

    // Rotation for sparkle effect
    particleSystem.minAngularSpeed = -Math.PI * 0.5;
    particleSystem.maxAngularSpeed = Math.PI * 0.5;

    // Very low emit power so particles stay close to shield surface
    particleSystem.minEmitPower = 0.2;
    particleSystem.maxEmitPower = 0.5;
    particleSystem.updateSpeed = 0.02;

    return particleSystem;
  }

  /**
   * Creates a burst effect when the shield disappears
   * Particles explode outward from the shield sphere
   * @param position Center position of the burst
   */
  createShieldBurstEffect(position: Vector3): void {
    // Main burst - cyan energy particles exploding outward
    const burstSystem = Particles.createParticleSystem('shieldBurst', 200, this.level, false);
    burstSystem.particleTexture = new Texture('assets/particles/flare.png', this.level.scene);
    burstSystem.emitter = position;

    // Emit from the shield sphere surface
    burstSystem.createSphereEmitter(1.25, 0); // Match shield radius

    // Cyan/white energy colors matching the shield
    burstSystem.addColorGradient(0, new Color4(0.3, 0.9, 1.0, 1.0));
    burstSystem.addColorGradient(0.3, new Color4(0.5, 0.95, 1.0, 0.9));
    burstSystem.addColorGradient(0.7, new Color4(0.8, 1.0, 1.0, 0.5));
    burstSystem.addColorGradient(1.0, new Color4(1.0, 1.0, 1.0, 0.0));

    // Size gradient - start medium, shrink
    burstSystem.addSizeGradient(0, 0.4);
    burstSystem.addSizeGradient(0.5, 0.25);
    burstSystem.addSizeGradient(1.0, 0.0);

    burstSystem.minSize = 0.2;
    burstSystem.maxSize = 0.5;

    // Short lifespan for snappy effect
    burstSystem.minLifeTime = 0.4;
    burstSystem.maxLifeTime = 0.8;

    // One-time burst emission
    burstSystem.emitRate = 0;
    burstSystem.manualEmitCount = 200;

    // Additive blending for glow
    burstSystem.blendMode = ParticleSystem.BLENDMODE_ADD;

    // Slight upward drift
    burstSystem.gravity = new Vector3(0, 1, 0);

    // Rotation for sparkle
    burstSystem.minAngularSpeed = -Math.PI;
    burstSystem.maxAngularSpeed = Math.PI;

    // Fast outward movement
    burstSystem.minEmitPower = 3;
    burstSystem.maxEmitPower = 6;
    burstSystem.updateSpeed = 0.02;

    // Drag to slow down particles
    burstSystem.addDragGradient(0, 0.3);
    burstSystem.addDragGradient(1.0, 0.8);

    // Auto-dispose
    burstSystem.disposeOnStop = true;
    burstSystem.targetStopDuration = 0.4;
    burstSystem.start();

    // Secondary sparkle ring - smaller particles at the edges
    const sparkleSystem = Particles.createParticleSystem('shieldSparkle', 100, this.level, false);
    sparkleSystem.particleTexture = new Texture('assets/particles/flare.png', this.level.scene);
    sparkleSystem.emitter = position;

    sparkleSystem.createSphereEmitter(1.3, 0.1);

    sparkleSystem.color1 = new Color4(0.9, 1.0, 1.0, 1.0);
    sparkleSystem.color2 = new Color4(0.6, 0.95, 1.0, 0.8);
    sparkleSystem.colorDead = new Color4(1.0, 1.0, 1.0, 0.0);

    sparkleSystem.minSize = 0.1;
    sparkleSystem.maxSize = 0.2;
    sparkleSystem.minLifeTime = 0.3;
    sparkleSystem.maxLifeTime = 0.5;
    sparkleSystem.emitRate = 0;
    sparkleSystem.manualEmitCount = 100;
    sparkleSystem.blendMode = ParticleSystem.BLENDMODE_ADD;
    sparkleSystem.minEmitPower = 5;
    sparkleSystem.maxEmitPower = 8;
    sparkleSystem.updateSpeed = 0.02;
    sparkleSystem.disposeOnStop = true;
    sparkleSystem.targetStopDuration = 0.3;
    sparkleSystem.start();
  }

  createDeathParticleSystem(position: Vector3): void {
    // 1. Core Burst (Bright Flash)
    const burstSystem = Particles.createParticleSystem('deathBurst', 100, this.level, false);
    burstSystem.particleTexture = new Texture('assets/particles/circle_05.png', this.level.scene);
    burstSystem.emitter = position;
    burstSystem.createSphereEmitter(0.5);

    burstSystem.color1 = new Color4(1, 0.9, 0.5, 1);
    burstSystem.color2 = new Color4(1, 0.5, 0.2, 1);
    burstSystem.colorDead = new Color4(1, 0, 0, 0);

    burstSystem.minSize = 1.0;
    burstSystem.maxSize = 2.0;
    burstSystem.minLifeTime = 0.2;
    burstSystem.maxLifeTime = 0.4;
    burstSystem.emitRate = 0;
    burstSystem.manualEmitCount = 100;
    burstSystem.blendMode = ParticleSystem.BLENDMODE_ADD;
    burstSystem.minEmitPower = 1;
    burstSystem.maxEmitPower = 2.5;

    burstSystem.updateSpeed = 0.05;
    burstSystem.disposeOnStop = true;
    burstSystem.targetStopDuration = 0.2;
    burstSystem.start();

    // 2. Debris / Sparks (Scattering)
    const debrisSystem = Particles.createParticleSystem('deathDebris', 200, this.level, false);
    debrisSystem.particleTexture = new Texture('assets/particles/circle_05.png', this.level.scene);
    debrisSystem.emitter = position;
    debrisSystem.createSphereEmitter(1);

    debrisSystem.color1 = new Color4(1, 0.2, 0.2, 1);
    debrisSystem.color2 = new Color4(1, 0.5, 0, 1);
    debrisSystem.colorDead = new Color4(0.2, 0.2, 0.2, 0); // Fade to smoke color

    debrisSystem.minSize = 0.3;
    debrisSystem.maxSize = 0.7;
    debrisSystem.minLifeTime = 0.8;
    debrisSystem.maxLifeTime = 1.5;
    debrisSystem.emitRate = 0;
    debrisSystem.manualEmitCount = 200;
    debrisSystem.blendMode = ParticleSystem.BLENDMODE_ADD;
    debrisSystem.gravity = new Vector3(0, -9.81, 0); // Fall down
    debrisSystem.minEmitPower = 2.5;
    debrisSystem.maxEmitPower = 5;
    debrisSystem.updateSpeed = 0.02;
    debrisSystem.disposeOnStop = true;
    debrisSystem.targetStopDuration = 0.8;
    debrisSystem.start();

    // 3. Smoke Ring (Expanding)
    const smokeSystem = Particles.createParticleSystem('deathSmoke', 50, this.level, false);
    smokeSystem.particleTexture = new Texture('assets/particles/smoke2.png', this.level.scene);
    smokeSystem.emitter = position;
    // Emit from center
    smokeSystem.createSphereEmitter(0.5);

    smokeSystem.color1 = new Color4(0.5, 0.5, 0.5, 0.5);
    smokeSystem.color2 = new Color4(0.3, 0.3, 0.3, 0.5);
    smokeSystem.colorDead = new Color4(0, 0, 0, 0);

    smokeSystem.minSize = 1.0;
    smokeSystem.maxSize = 3.0; // Grows big
    smokeSystem.addSizeGradient(0, 1.0);
    smokeSystem.addSizeGradient(1.0, 3.0);

    smokeSystem.minLifeTime = 0.5;
    smokeSystem.maxLifeTime = 1.0;
    smokeSystem.emitRate = 0;
    smokeSystem.manualEmitCount = 50;
    smokeSystem.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    smokeSystem.minEmitPower = 0.5;
    smokeSystem.maxEmitPower = 1.5;
    smokeSystem.updateSpeed = 0.01;
    smokeSystem.disposeOnStop = true;
    smokeSystem.targetStopDuration = 0.75;
    smokeSystem.start();
  }

  /**
   * Creates a fire column particle system for fire trap obstacles
   * @param emitter The mesh or position to emit from
   * @param height The height/length of the fire column
   * @param axis The axis to emit along ('x', 'y', or 'z')
   * @param direction The direction (1 = positive, -1 = negative)
   * @param width The width/spread of the fire beam (default 1)
   * @returns The created ParticleSystem (not started - start/stop controlled by behavior)
   */
  createFireColumnParticleSystem(
    emitter: AbstractMesh | Vector3,
    height: number,
    axis: 'x' | 'y' | 'z' = 'y',
    direction: 1 | -1 = 1,
    width: number = 1
  ): ParticleSystem | GPUParticleSystem {
    const particleSystem = Particles.createParticleSystem('fireColumn', 300, this.level);

    // Fire texture
    particleSystem.particleTexture = new Texture('assets/particles/fire.jpg', this.level.scene);
    particleSystem.emitter = emitter;

    // Calculate direction vector based on axis and direction
    let directionVector = new Vector3(0, 0, 0);
    let perpAxis1 = new Vector3(0, 0, 0);
    let perpAxis2 = new Vector3(0, 0, 0);

    switch (axis) {
      case 'x':
        directionVector = new Vector3(direction, 0, 0);
        perpAxis1 = new Vector3(0, 1, 0);
        perpAxis2 = new Vector3(0, 0, 1);
        break;
      case 'y':
        directionVector = new Vector3(0, 1, 0); // Y always goes up
        perpAxis1 = new Vector3(1, 0, 0);
        perpAxis2 = new Vector3(0, 0, 1);
        break;
      case 'z':
        directionVector = new Vector3(0, 0, direction);
        perpAxis1 = new Vector3(1, 0, 0);
        perpAxis2 = new Vector3(0, 1, 0);
        break;
    }

    // Emit from a box at the base (perpendicular to fire direction) - size based on width
    const halfWidth = width / 2;
    particleSystem.minEmitBox = perpAxis1.scale(-halfWidth).add(perpAxis2.scale(-halfWidth));
    particleSystem.maxEmitBox = perpAxis1.scale(halfWidth).add(perpAxis2.scale(halfWidth)).add(directionVector.scale(0.5));

    // Particles shoot along the direction axis with some spread
    const spread = 0.3;
    particleSystem.direction1 = directionVector.add(perpAxis1.scale(-spread)).add(perpAxis2.scale(-spread));
    particleSystem.direction2 = directionVector.add(perpAxis1.scale(spread)).add(perpAxis2.scale(spread));

    // Gravity opposite to direction for flickering
    particleSystem.gravity = directionVector.scale(-1);

    // Fire colors: orange -> yellow -> red
    particleSystem.addColorGradient(0, new Color4(1.0, 0.5, 0.0, 1.0)); // Orange
    particleSystem.addColorGradient(0.3, new Color4(1.0, 0.8, 0.0, 0.9)); // Yellow-orange
    particleSystem.addColorGradient(0.6, new Color4(1.0, 0.3, 0.0, 0.6)); // Red-orange
    particleSystem.addColorGradient(1.0, new Color4(0.3, 0.1, 0.0, 0.0)); // Dark fade out

    // Size gradient - grow slightly then shrink
    particleSystem.addSizeGradient(0, 0.5);
    particleSystem.addSizeGradient(0.3, 1.0);
    particleSystem.addSizeGradient(1.0, 0.2);

    particleSystem.minSize = 0.8;
    particleSystem.maxSize = 1.5;

    // Lifespan based on height
    particleSystem.minLifeTime = height * 0.15;
    particleSystem.maxLifeTime = height * 0.25;

    // High emission rate for dense fire
    particleSystem.emitRate = 150;

    // Additive blending for glow
    particleSystem.blendMode = ParticleSystem.BLENDMODE_ADD;

    // Rotation for flickering effect
    particleSystem.minAngularSpeed = -Math.PI;
    particleSystem.maxAngularSpeed = Math.PI;

    // Emit power to reach the height
    particleSystem.minEmitPower = height * 0.8;
    particleSystem.maxEmitPower = height * 1.2;
    particleSystem.updateSpeed = 0.02;

    return particleSystem;
  }

  /**
   * Creates a beautiful particle effect when a checkpoint is activated
   * Includes an energy burst, rising sparkles, and an expanding ring
   * @param position Center position of the checkpoint
   */
  createCheckpointActivationEffect(position: Vector3): void {
    // 1. Main Energy Burst - Golden/green upward explosion
    const burstSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('checkpointBurst', 300, this.level, false);
    burstSystem.particleTexture = this.starTexture2;
    burstSystem.emitter = position;
    burstSystem.createSphereEmitter(1.5, 0);

    // Golden to green gradient for checkpoint activation
    burstSystem.addColorGradient(0, new Color4(1.0, 0.9, 0.3, 1.0)); // Bright gold
    burstSystem.addColorGradient(0.3, new Color4(0.6, 1.0, 0.4, 0.9)); // Yellow-green
    burstSystem.addColorGradient(0.7, new Color4(0.2, 0.9, 0.5, 0.6)); // Green
    burstSystem.addColorGradient(1.0, new Color4(0.1, 1.0, 0.6, 0.0)); // Fade to transparent

    burstSystem.addSizeGradient(0, 1);
    burstSystem.addSizeGradient(0.5, 0.75);
    burstSystem.addSizeGradient(1.0, 0.0);

    burstSystem.minSize = 0.3;
    burstSystem.maxSize = 0.8;
    burstSystem.minLifeTime = 0.6;
    burstSystem.maxLifeTime = 1.2;
    burstSystem.emitRate = 200;
    burstSystem.manualEmitCount = 200;

    burstSystem.blendMode = ParticleSystem.BLENDMODE_ADD;
    burstSystem.gravity = new Vector3(0, 3, 0); // Upward drift
    burstSystem.minAngularSpeed = -Math.PI;
    burstSystem.maxAngularSpeed = Math.PI;
    burstSystem.minEmitPower = 4;
    burstSystem.maxEmitPower = 8;
    burstSystem.updateSpeed = 0.02;
    burstSystem.addDragGradient(0, 0.2);
    burstSystem.addDragGradient(1.0, 0.6);
    // disposeOnStop = false: auto-dispose would call dispose(true) and delete the shared texture.
    burstSystem.disposeOnStop = false;
    burstSystem.start();
    setTimeout(() => {
      burstSystem.stop();
      burstSystem.dispose(false);
    }, 1500);

    // 2. Rising Sparkles - Slow rising golden particles
    const sparkleSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('checkpointSparkle', 150, this.level, false);

    sparkleSystem.particleTexture = this.starTexture3;
    sparkleSystem.emitter = position;
    sparkleSystem.createCylinderEmitter(1.5, 0.5, 0, 0); // Cylinder shape for vertical emission

    sparkleSystem.color1 = new Color4(1.0, 0.95, 0.5, 1.0); // Warm gold
    sparkleSystem.color2 = new Color4(1.0, 0.85, 0.3, 0.9); // Deep gold
    sparkleSystem.colorDead = new Color4(0.8, 1.0, 0.6, 0.0); // Fade to transparent green

    sparkleSystem.minSize = 0.15;
    sparkleSystem.maxSize = 0.35;
    sparkleSystem.addSizeGradient(0, 0.5);
    sparkleSystem.addSizeGradient(0.5, 0.1);
    sparkleSystem.addSizeGradient(1.0, 0.0);

    sparkleSystem.minLifeTime = 1.5;
    sparkleSystem.maxLifeTime = 2.5;
    sparkleSystem.emitRate = 150;
    sparkleSystem.manualEmitCount = 150;
    sparkleSystem.blendMode = ParticleSystem.BLENDMODE_ADD;
    sparkleSystem.gravity = new Vector3(0, 2, 0); // Float upward
    sparkleSystem.minAngularSpeed = -Math.PI * 0.3;
    sparkleSystem.maxAngularSpeed = Math.PI * 0.3;

    // Direction - mostly upward with slight spread
    sparkleSystem.direction1 = new Vector3(-0.5, 3, -0.5);
    sparkleSystem.direction2 = new Vector3(0.5, 5, 0.5);

    sparkleSystem.minEmitPower = 1;
    sparkleSystem.maxEmitPower = 3;
    sparkleSystem.updateSpeed = 0.02;
    sparkleSystem.addDragGradient(0, 0.1);
    sparkleSystem.addDragGradient(1.0, 0.4);
    // disposeOnStop = false: auto-dispose would delete the shared texture.
    sparkleSystem.disposeOnStop = false;
    sparkleSystem.start();
    setTimeout(() => {
      sparkleSystem.stop();
      sparkleSystem.dispose(false);
    }, 4000);

    // 3. Expanding Ring - Quick horizontal ring burst
    const ringSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('checkpointRing', 100, this.level, false);
    ringSystem.particleTexture = this.starTexture4;
    ringSystem.emitter = position.add(new Vector3(0, 0.5, 0)); // Slightly above ground

    // Create a thin disc/ring emission pattern
    ringSystem.minEmitBox = new Vector3(-0.1, 0, -0.1);
    ringSystem.maxEmitBox = new Vector3(0.1, 0, 0.1);

    ringSystem.addColorGradient(0, new Color4(0.4, 1.0, 0.6, 1.0)); // Bright green
    ringSystem.addColorGradient(0.5, new Color4(0.8, 1.0, 0.5, 0.7)); // Yellow-green
    ringSystem.addColorGradient(1.0, new Color4(1.0, 1.0, 0.8, 0.0)); // Fade

    ringSystem.minSize = 0.3;
    ringSystem.maxSize = 0.6;
    ringSystem.addSizeGradient(0, 1);
    ringSystem.addSizeGradient(1.0, 0.0);

    ringSystem.minLifeTime = 0.4;
    ringSystem.maxLifeTime = 0.7;
    ringSystem.emitRate = 10;
    ringSystem.manualEmitCount = 10;
    ringSystem.blendMode = ParticleSystem.BLENDMODE_ADD;

    // Horizontal outward expansion
    ringSystem.direction1 = new Vector3(-1, 0.2, -1);
    ringSystem.direction2 = new Vector3(1, 0.4, 1);

    ringSystem.minEmitPower = 8;
    ringSystem.maxEmitPower = 12;
    ringSystem.updateSpeed = 0.02;
    ringSystem.addDragGradient(0, 0.3);
    ringSystem.addDragGradient(1.0, 0.8);
    // disposeOnStop = false: auto-dispose would delete the shared texture.
    ringSystem.disposeOnStop = false;
    ringSystem.start();
    setTimeout(() => {
      ringSystem.stop();
      ringSystem.dispose(false);
    }, 1500);

    // 4. Core Flash - Bright center flash
    const flashSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('checkpointFlash', 30, this.level, false);
    flashSystem.particleTexture = this.starTexture;
    flashSystem.emitter = position.add(new Vector3(0, 1, 0));
    flashSystem.createSphereEmitter(0.3);

    flashSystem.color1 = new Color4(1.0, 1.0, 0.9, 1.0); // Almost white
    flashSystem.color2 = new Color4(1.0, 1.0, 0.7, 1.0); // Warm white
    flashSystem.colorDead = new Color4(1.0, 1.0, 1.0, 0.0);

    flashSystem.minSize = 1.5;
    flashSystem.maxSize = 3.0;
    flashSystem.addSizeGradient(0, 2.0);
    flashSystem.addSizeGradient(0.2, 1.5);
    flashSystem.addSizeGradient(1.0, 0.0);

    flashSystem.minLifeTime = 0.2;
    flashSystem.maxLifeTime = 0.4;
    flashSystem.emitRate = 30;
    flashSystem.manualEmitCount = 30;
    flashSystem.blendMode = ParticleSystem.BLENDMODE_ADD;
    flashSystem.minEmitPower = 0.1;
    flashSystem.maxEmitPower = 0.5;
    flashSystem.updateSpeed = 0.02;
    // disposeOnStop = false: auto-dispose would delete the shared texture (starTexture).
    flashSystem.disposeOnStop = false;
    flashSystem.start();
    setTimeout(() => {
      flashSystem.stop();
      flashSystem.dispose(false);
    }, 700);
  }

  /**
   * Creates a spectacular firework celebration effect for level completion
   * Multiple bursts with trails, sparkles, and colorful explosions
   * @param position Center position for the fireworks
   * @param duration Duration in seconds for the firework show
   */
  createLevelCompleteFireworks(position: Vector3, duration: number = 5): void {
    const colors = [
      new Color4(1.0, 0.3, 0.3, 1.0), // Red
      new Color4(0.3, 1.0, 0.3, 1.0), // Green
      new Color4(0.3, 0.5, 1.0, 1.0), // Blue
      new Color4(1.0, 1.0, 0.3, 1.0), // Yellow
      new Color4(1.0, 0.3, 1.0, 1.0), // Magenta
      new Color4(0.3, 1.0, 1.0, 1.0), // Cyan
      new Color4(1.0, 0.6, 0.2, 1.0), // Orange
      new Color4(0.8, 0.4, 1.0, 1.0), // Purple
    ];

    // Immediate first burst at player position
    this.createFireworkBurst(position.add(new Vector3(0, 3, 0)), colors[0]);

    // Launch multiple firework bursts over time
    const burstCount = 4;
    const interval = (duration * 1000) / burstCount;

    for (let i = 1; i < burstCount; i++) {
      setTimeout(() => {
        // Random offset from center position
        const offsetX = (Math.random() - 0.5) * 6;
        const offsetZ = (Math.random() - 0.5) * 6;
        const burstHeight = 2 + Math.random() * 3;
        const burstPos = position.add(new Vector3(offsetX, burstHeight, offsetZ));

        // Random color for this burst
        const color = colors[Math.floor(Math.random() * colors.length)];

        this.createFireworkBurst(burstPos, color);
      }, i * interval);
    }

    // Also create some ground-level sparklers
    this.createSparklerEffect(position, duration);
  }

  /**
   * Creates a single firework burst explosion
   */
  private createFireworkBurst(position: Vector3, color: Color4): void {
    // Main explosion particles
    const burstSystem = Particles.createParticleSystem('fireworkBurst', 50, this.level, false);
    burstSystem.particleTexture = new Texture('assets/particles/light_03.png', this.level.scene);
    burstSystem.emitter = position;
    burstSystem.createSphereEmitter(0.5, 1); // Small origin, full sphere

    // Color gradient from bright to fade
    const brightColor = new Color4(Math.min(color.r, 1), Math.min(color.g, 1), Math.min(color.b, 1), 0.2);
    burstSystem.addColorGradient(0, brightColor);
    burstSystem.addColorGradient(0.2, color);
    burstSystem.addColorGradient(0.7, new Color4(color.r * 0.5, color.g * 0.5, color.b * 0.5, 0.3));
    burstSystem.addColorGradient(1.0, new Color4(0.1, 0.1, 0.1, 0.0));

    // Size gradient - bigger particles
    burstSystem.addSizeGradient(0, 0.6);
    burstSystem.addSizeGradient(0.1, 1.0);
    burstSystem.addSizeGradient(0.5, 0.6);
    burstSystem.addSizeGradient(1.0, 0.0);

    burstSystem.minSize = 0.05;
    burstSystem.maxSize = 0.1;
    burstSystem.minLifeTime = 1.0;
    burstSystem.maxLifeTime = 2.5;
    burstSystem.manualEmitCount = 50;
    burstSystem.blendMode = ParticleSystem.BLENDMODE_ADD;
    burstSystem.gravity = new Vector3(0, -2, 0); // Slower fall
    burstSystem.minAngularSpeed = -Math.PI;
    burstSystem.maxAngularSpeed = Math.PI;
    burstSystem.minEmitPower = 5;
    burstSystem.maxEmitPower = 10;
    burstSystem.addDragGradient(0, 0.1);
    burstSystem.addDragGradient(0.5, 0.4);
    burstSystem.addDragGradient(1.0, 0.8);
    burstSystem.disposeOnStop = true;
    burstSystem.start();

    // Secondary sparkle ring - smaller white sparkles
    const sparkleSystem = Particles.createParticleSystem('fireworkSparkle', 50, this.level, false);
    sparkleSystem.particleTexture = new Texture('assets/particles/flare.png', this.level.scene);
    sparkleSystem.emitter = position;
    sparkleSystem.createSphereEmitter(0.2, 1);

    sparkleSystem.addColorGradient(0, new Color4(1.0, 1.0, 1.0, 0.5));
    sparkleSystem.addColorGradient(0.5, new Color4(1.0, 1.0, 0.8, 0.25));
    sparkleSystem.addColorGradient(1.0, new Color4(1.0, 1.0, 1.0, 0.0));

    sparkleSystem.minSize = 0.05;
    sparkleSystem.maxSize = 0.1;
    sparkleSystem.minLifeTime = 0.5;
    sparkleSystem.maxLifeTime = 1.2;
    sparkleSystem.manualEmitCount = 50;
    sparkleSystem.blendMode = ParticleSystem.BLENDMODE_ADD;
    sparkleSystem.gravity = new Vector3(0, -4, 0);
    sparkleSystem.minEmitPower = 4;
    sparkleSystem.maxEmitPower = 8;
    sparkleSystem.disposeOnStop = true;
    sparkleSystem.start();

    // Trailing particles - follow the main burst
    const trailSystem = Particles.createParticleSystem('fireworkTrail', 50, this.level, false);
    trailSystem.particleTexture = new Texture('assets/particles/flare.png', this.level.scene);
    trailSystem.emitter = position;
    trailSystem.createSphereEmitter(0.5, 1);

    trailSystem.addColorGradient(0, new Color4(1.0, 0.9, 0.6, 0.5)); // Golden
    trailSystem.addColorGradient(0.5, new Color4(1.0, 0.6, 0.2, 0.25)); // Orange
    trailSystem.addColorGradient(1.0, new Color4(0.5, 0.2, 0.0, 0.0)); // Fade

    trailSystem.addSizeGradient(0, 0.15);
    trailSystem.addSizeGradient(1.0, 0.0);

    trailSystem.minSize = 0.05;
    trailSystem.maxSize = 0.1;
    trailSystem.minLifeTime = 0.8;
    trailSystem.maxLifeTime = 1.5;
    trailSystem.manualEmitCount = 50;
    trailSystem.blendMode = ParticleSystem.BLENDMODE_ADD;
    trailSystem.gravity = new Vector3(0, -5, 0);
    trailSystem.minEmitPower = 3;
    trailSystem.maxEmitPower = 7;
    trailSystem.disposeOnStop = true;
    trailSystem.start();
  }

  /**
   * Creates ground-level continuous sparkler effect
   */
  private createSparklerEffect(position: Vector3, duration: number): void {
    // Multiple sparkler fountains around the player
    const sparklerPositions = [
      position.add(new Vector3(-2, 0, -2)),
      position.add(new Vector3(2, 0, -2)),
      position.add(new Vector3(-2, 0, 2)),
      position.add(new Vector3(2, 0, 2)),
    ];

    for (const pos of sparklerPositions) {
      const sparkler = Particles.createParticleSystem('sparkler', 100, this.level);

      sparkler.particleTexture = new Texture('assets/particles/flare.png', this.level.scene);
      sparkler.emitter = pos;

      // Fountain emission - narrow cone upward
      sparkler.createConeEmitter(0.3, Math.PI / 8);

      // Golden sparkle colors
      sparkler.addColorGradient(0, new Color4(1.0, 1.0, 0.8, 0.7));
      sparkler.addColorGradient(0.3, new Color4(1.0, 0.8, 0.3, 0.6));
      sparkler.addColorGradient(0.7, new Color4(1.0, 0.5, 0.1, 0.4));
      sparkler.addColorGradient(1.0, new Color4(0.8, 0.3, 0.0, 0.0));

      sparkler.addSizeGradient(0, 0.15);
      sparkler.addSizeGradient(0.5, 0.1);
      sparkler.addSizeGradient(1.0, 0.0);

      sparkler.minSize = 0.08;
      sparkler.maxSize = 0.15;
      sparkler.minLifeTime = 0.8;
      sparkler.maxLifeTime = 1.5;
      sparkler.emitRate = 50;
      sparkler.blendMode = ParticleSystem.BLENDMODE_ADD;
      sparkler.gravity = new Vector3(0, -2, 0);
      sparkler.minAngularSpeed = -Math.PI * 2;
      sparkler.maxAngularSpeed = Math.PI * 2;
      sparkler.minEmitPower = 4;
      sparkler.maxEmitPower = 7;
      sparkler.updateSpeed = 0.02;
      sparkler.targetStopDuration = duration;
      sparkler.disposeOnStop = true;
      sparkler.start();
    }

    // Central golden fountain
    const centralFountain = Particles.createParticleSystem('centralFountain', 500, this.level);
    centralFountain.particleTexture = new Texture('assets/particles/flare.png', this.level.scene);
    centralFountain.emitter = position;
    centralFountain.createConeEmitter(0.5, Math.PI / 6);

    centralFountain.addColorGradient(0, new Color4(1.0, 1.0, 1.0, 1.0));
    centralFountain.addColorGradient(0.2, new Color4(1.0, 0.9, 0.5, 1.0));
    centralFountain.addColorGradient(0.6, new Color4(1.0, 0.6, 0.2, 0.7));
    centralFountain.addColorGradient(1.0, new Color4(1.0, 0.3, 0.0, 0.0));

    centralFountain.addSizeGradient(0, 0.2);
    centralFountain.addSizeGradient(0.5, 0.15);
    centralFountain.addSizeGradient(1.0, 0.0);

    centralFountain.minSize = 0.1;
    centralFountain.maxSize = 0.25;
    centralFountain.minLifeTime = 1.0;
    centralFountain.maxLifeTime = 2.0;
    centralFountain.emitRate = 150;
    centralFountain.blendMode = ParticleSystem.BLENDMODE_ADD;
    centralFountain.gravity = new Vector3(0, -1, 0);
    centralFountain.minAngularSpeed = -Math.PI;
    centralFountain.maxAngularSpeed = Math.PI;
    centralFountain.minEmitPower = 6;
    centralFountain.maxEmitPower = 10;
    centralFountain.updateSpeed = 0.02;
    centralFountain.targetStopDuration = duration;
    centralFountain.disposeOnStop = true;
    centralFountain.start();
  }

  static createStarParticleSystem(position: Vector3, game: Game): ParticleSystem | GPUParticleSystem {
    const particleSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('stars', 500, game);

    // Use star texture with alpha transparency enabled
    const texture = new Texture('assets/particles/star.png', game.scene);
    texture.hasAlpha = true;

    particleSystem.particleTexture = texture;
    particleSystem.emitter = position;

    particleSystem.minSize = 0.3;
    particleSystem.maxSize = 0.6;
    particleSystem.emitRate = 100;
    particleSystem.targetStopDuration = 0.5;
    particleSystem.minLifeTime = 1;
    particleSystem.maxLifeTime = 3;

    particleSystem.particleEmitterType = new ConeParticleEmitter(0.5, Math.PI / 3);

    particleSystem.minAngularSpeed = -Math.PI;
    particleSystem.maxAngularSpeed = Math.PI;
    particleSystem.minEmitPower = 8;
    particleSystem.maxEmitPower = 15;

    // Rainbow colors
    const alpha = 0.6;
    particleSystem.addColorGradient(0, new Color4(1, 0.2, 0.2, alpha));
    particleSystem.addColorGradient(0.2, new Color4(1, 1, 0.2, alpha));
    particleSystem.addColorGradient(0.4, new Color4(0.2, 1, 0.2, alpha));
    particleSystem.addColorGradient(0.6, new Color4(0.2, 1, 1, alpha));
    particleSystem.addColorGradient(0.8, new Color4(0.2, 0.2, 1, alpha));
    particleSystem.addColorGradient(1.0, new Color4(1, 0.2, 1, alpha));

    particleSystem.minInitialRotation = 0;
    particleSystem.maxInitialRotation = Math.PI * 2;
    particleSystem.gravity = new Vector3(0, -9.81, 0);
    particleSystem.addDragGradient(0, 0.2);
    particleSystem.addDragGradient(1.0, 0.8);
    particleSystem.updateSpeed = 0.02;
    particleSystem.blendMode = ParticleSystem.BLENDMODE_ADD;
    particleSystem.disposeOnStop = true;

    return particleSystem;
  }

  /**
   * Creates a glowing portal effect for the goal ring
   * Displays a bright disc or energy particles inside the portal when activated
   * @param position Center position of the goal portal
   * @returns The created ParticleSystem
   */
  createGoalPortalEffect(position: Vector3): ParticleSystem | GPUParticleSystem {
    const particleSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('goalPortal', 500, this.level);

    // Use flare texture for bright glowing effect
    particleSystem.particleTexture = new Texture('assets/particles/smoke.png', this.level.scene);

    // Position slightly above the base of the portal
    const emitPosition = position.clone();
    //emitPosition.y += 2; // Center of the hexagonal ring
    particleSystem.emitter = emitPosition;

    // Create a flat disc/cylinder emission area to fill the portal ring
    // Emit in a circular pattern within the ring
    const boxWidth = 1.25;
    const boxHeight = 1;
    particleSystem.minEmitBox = new Vector3(-boxWidth, -boxHeight, -boxWidth);
    particleSystem.maxEmitBox = new Vector3(boxWidth, boxHeight, boxWidth);

    // Cyan/white energy colors that pulse and glow
    particleSystem.addColorGradient(0, new Color4(0.2, 0.8, 1.0, 0.0)); // Start transparent
    particleSystem.addColorGradient(0.2, new Color4(0.4, 0.9, 1.0, 0.9)); // Bright cyan
    particleSystem.addColorGradient(0.5, new Color4(0.8, 0.95, 1.0, 1.0)); // Near white
    particleSystem.addColorGradient(0.8, new Color4(0.6, 0.9, 1.0, 0.8)); // Back to cyan
    particleSystem.addColorGradient(1.0, new Color4(1.0, 1.0, 1.0, 0.0)); // Fade out

    // Size - create a dense glowing disc effect
    particleSystem.minSize = 0.3;
    particleSystem.maxSize = 0.8;

    // Size gradient - grow then shrink
    particleSystem.addSizeGradient(0, 0.1);
    particleSystem.addSizeGradient(0.3, 0.6);
    particleSystem.addSizeGradient(0.7, 0.4);
    particleSystem.addSizeGradient(1.0, 0.0);

    // Short lifespan for dynamic, pulsing effect
    particleSystem.minLifeTime = 0.5;
    particleSystem.maxLifeTime = 1.0;

    // High emission rate for dense, glowing disc
    particleSystem.emitRate = 200;

    // Additive blending for bright glow
    particleSystem.blendMode = ParticleSystem.BLENDMODE_ADD;

    // Slight upward movement through the portal
    particleSystem.direction1 = new Vector3(-0.5, 0.5, -0.5);
    particleSystem.direction2 = new Vector3(0.5, 2, 0.5);

    // No gravity - particles float
    particleSystem.gravity = new Vector3(0, 0, 0);

    // Rotation for sparkle effect
    particleSystem.minAngularSpeed = -Math.PI * 0.5;
    particleSystem.maxAngularSpeed = Math.PI * 0.5;

    // Low emit power - particles stay within the ring
    particleSystem.minEmitPower = 0.5;
    particleSystem.maxEmitPower = 1.5;
    particleSystem.updateSpeed = 0.02;

    // Start the effect
    particleSystem.start();

    return particleSystem;
  }

  createStarGlitterParticleSystem(position: Vector3, level: Level, autoStart: boolean = true): ParticleSystem | GPUParticleSystem {
    const particles: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('starGlitter', 100, level);

    particles.particleTexture = this.starTexture3.clone();
    particles.emitter = position;
    const emitBoxSize = 0.4;
    particles.minEmitBox = new Vector3(-emitBoxSize, -emitBoxSize, -emitBoxSize);
    particles.maxEmitBox = new Vector3(emitBoxSize, emitBoxSize, emitBoxSize);

    particles.color1 = new Color4(1, 1, 0, 1.0);
    particles.color2 = new Color4(1, 0.5, 0, 1.0);
    particles.colorDead = new Color4(0, 0, 0, 0.0);

    particles.minSize = 0.4;
    particles.maxSize = 0.6;

    particles.minLifeTime = 0.2;
    particles.maxLifeTime = 0.4;

    particles.emitRate = 50;

    particles.blendMode = ParticleSystem.BLENDMODE_ADD;

    particles.gravity = new Vector3(0, 0, 0);
    particles.direction1 = new Vector3(-1, -1, -1);
    particles.direction2 = new Vector3(1, 1, 1);

    particles.minAngularSpeed = 0;
    particles.maxAngularSpeed = Math.PI;

    particles.minEmitPower = 0.5;
    particles.maxEmitPower = 1.0;

    if (autoStart) particles.start();
    return particles;
  }

  createFireflyParticleSystem(emitRateFactor: number = 1, autoStart: boolean = true): ParticleSystem | GPUParticleSystem {
    const maxEmitRate = 2000;
    const emitRate = maxEmitRate * emitRateFactor;

    const particleSystem: ParticleSystem | GPUParticleSystem = Particles.createParticleSystem('fireflies', maxEmitRate, this.level);

    particleSystem.particleTexture = new Texture('assets/particles/circle_05.png', this.level.scene);

    // Cast to any to avoid type check issues if Camera is passed,
    // assuming BabylonJS runtime handles Node/Camera or we might need an updater.
    // If it doesn't work, we'll see no particles moving with camera.
    particleSystem.emitter = this.level.scene.activeCamera as any;

    // Emit from a large box around the player/emitter
    // We want them to fill the environment
    const range = 25;
    particleSystem.minEmitBox = new Vector3(-range, -10, -range);
    particleSystem.maxEmitBox = new Vector3(range, 20, range);

    // Color: Firefly Yellow/Green
    // Make them pulse slightly by having slightly different start/end colors or alpha
    particleSystem.color1 = new Color4(0.8, 1.0, 0.4, 1.0); // Bright Yellow-Green
    particleSystem.color2 = new Color4(0.6, 0.9, 0.2, 0.8); // Slightly dimmer
    particleSystem.colorDead = new Color4(0.8, 1.0, 0.4, 0.0);

    // Size
    particleSystem.minSize = 0.2;
    particleSystem.maxSize = 0.3;

    // Life time
    particleSystem.minLifeTime = 2.0;
    particleSystem.maxLifeTime = 3.0;

    // Emission rate
    particleSystem.emitRate = emitRate;

    // Blend mode
    particleSystem.blendMode = ParticleSystem.BLENDMODE_ADD;

    // Movement: Slow floating
    particleSystem.gravity = new Vector3(0, 0.2, 0); // Slight updraft
    // Random direction
    particleSystem.direction1 = new Vector3(-1, -1, -1);
    particleSystem.direction2 = new Vector3(1, 1, 1);
    particleSystem.minEmitPower = 0.5;
    particleSystem.maxEmitPower = 1.0;
    particleSystem.updateSpeed = 0.005;

    // Add noise for erratic movement
    const noiseTexture = new NoiseProceduralTexture('perlin', 256, this.level.game.scene);

    particleSystem.noiseTexture = noiseTexture;
    particleSystem.noiseStrength = new Vector3(2, 2, 2);

    if (autoStart) particleSystem.start();

    return particleSystem;
  }

  createBirdsParticleSystem(capacity: number, autoStart: boolean = true): ParticleSystem | GPUParticleSystem {
    const particleSystem = Particles.createParticleSystem('birds', capacity, this.level);
    particleSystem.particleTexture = new Texture('assets/particles/birds.png', this.level.scene, false, false);

    particleSystem.isAnimationSheetEnabled = true;
    particleSystem.spriteCellWidth = 600 / 5; // 120
    particleSystem.spriteCellHeight = 628 / 4; // 157
    particleSystem.startSpriteCellID = 0;
    particleSystem.endSpriteCellID = 21;
    particleSystem.spriteCellLoop = true;
    particleSystem.spriteCellChangeSpeed = 20; // Faster animation

    const heightOffset = 50;

    // Emitter is the player's model or a fallback Vector3
    // Using a Mesh emitter ensures it tracks properly
    particleSystem.emitter = this.level.scene.activeCamera.position;

    // Emit from a box above the player, smaller range so they are closer
    const rangeX = 80;
    const rangeZ = 80;
    particleSystem.minEmitBox = new Vector3(-rangeX, heightOffset, -rangeZ);
    particleSystem.maxEmitBox = new Vector3(rangeX, heightOffset + 10, rangeZ);

    // Dark silhouettes
    particleSystem.color1 = new Color4(0.1, 0.1, 0.12, 0.8);
    particleSystem.color2 = new Color4(0.05, 0.05, 0.05, 0.6);
    particleSystem.colorDead = new Color4(0.1, 0.1, 0.12, 0.0);

    // Size - needs to be large because they are very far away (height ~ 120)
    particleSystem.minSize = 2.0;
    particleSystem.maxSize = 4.0;

    // Long life time so they cross the sky
    particleSystem.minLifeTime = 15.0;
    particleSystem.maxLifeTime = 25.0;

    // Emission rate: steady stream over lifetime instead of an initial burst
    // Average lifetime is 20s, so capacity / 20.0 gives steady spawn
    particleSystem.emitRate = capacity / 20.0;

    // Alpha blending
    particleSystem.blendMode = ParticleSystem.BLENDMODE_STANDARD;

    // Movement: Fly steadily in one direction (e.g., across the x/z plane)
    particleSystem.gravity = new Vector3(0, 0, 0);
    particleSystem.direction1 = new Vector3(10, 0, 5);
    particleSystem.direction2 = new Vector3(15, 2, 10);

    // Speed
    particleSystem.minEmitPower = 0.4;
    particleSystem.maxEmitPower = 0.8;
    particleSystem.updateSpeed = 0.01;

    // Very slight rotation for banking
    // particleSystem.minAngularSpeed = -0.1;
    // particleSystem.maxAngularSpeed = 0.1;

    // We can use a noise texture to make them flap/wobble slightly
    // Removed noise texture since we have actual wing animation now
    // const noiseTexture = new NoiseProceduralTexture('bird_noise', 256, this.level.scene);
    // particleSystem.noiseTexture = noiseTexture;
    // particleSystem.noiseStrength = new Vector3(1, 1, 1);

    if (autoStart) particleSystem.start();

    return particleSystem;
  }
}
